import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import {
  approvePCEnrollmentSession,
  getPCEnrollmentOwnerView,
  type OwnerViewResponse,
} from "../api/pcEnrollment";
import { useAuth } from "../stores/auth";

// Mobile-friendly approve page. Owner lands here after scanning a QR
// from the PC's /join screen. Shows the context the threat model
// requires (hostname, IP, user-agent, time, countdown) so the owner
// can verify it's really their PC, then asks for the 4-digit OOB code
// shown on the PC screen.
//
// The OOB code is load-bearing security — a network attacker can
// create a pending session but can't see the legit PC's screen, so
// they can't supply the right OOB.
export default function ApprovePC() {
  const { code } = useParams<{ code: string }>();
  const accessToken = useAuth((s) => s.accessToken);
  const refreshToken = useAuth((s) => s.refreshToken);
  const tenant = useAuth((s) => s.tenant);
  const role = useAuth((s) => s.role);

  const [info, setInfo] = useState<OwnerViewResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [oob, setOob] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [approved, setApproved] = useState(false);
  const [now, setNow] = useState<number>(Date.now());

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Tick the clock every second for the countdown.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const loadInfo = useCallback(async () => {
    if (!code) return;
    try {
      const data = await getPCEnrollmentOwnerView(code);
      if (mounted.current) setInfo(data);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string }; status?: number } })
        ?.response;
      if (detail?.status === 404) {
        if (mounted.current) setLoadError("This setup link is no longer valid.");
      } else {
        if (mounted.current)
          setLoadError(detail?.data?.detail ?? "Couldn't load this request.");
      }
    }
  }, [code]);

  useEffect(() => {
    if (accessToken || refreshToken) loadInfo();
  }, [accessToken, refreshToken, loadInfo]);

  if (!accessToken && !refreshToken) {
    // Not logged in → bounce to login, then back here after.
    const dest = code ? `/approve/${code}` : "/dashboard";
    return <Navigate to="/login" state={{ from: dest }} replace />;
  }

  // Only owners can approve new PCs per threat model.
  if (role && role !== "owner") {
    return (
      <Layout>
        <Card>
          <h1 className="text-lg font-semibold text-slate-900">
            Only the office owner can approve new computers.
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Ask the owner of {tenant?.name ?? "this office"} to scan the QR on
            the PC's screen.
          </p>
        </Card>
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout>
        <Card tone="warn">
          <p className="font-medium text-red-900">{loadError}</p>
          <p className="mt-1 text-sm text-red-800">
            Ask the PC to refresh its setup page and try again.
          </p>
          <Link
            to="/computers"
            className="no-tap-highlight mt-4 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Back to your computers
          </Link>
        </Card>
      </Layout>
    );
  }

  if (!info) {
    return (
      <Layout>
        <Card>
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
          </div>
        </Card>
      </Layout>
    );
  }

  if (info.status === "approved" || info.status === "claimed" || approved) {
    return (
      <Layout>
        <Card tone="ok">
          <p className="text-lg font-medium text-green-900">Approved ✓</p>
          <p className="mt-1 text-sm text-green-800">
            The PC's setup page is downloading the installer now. You can put
            your phone down.
          </p>
          <Link
            to="/computers"
            className="no-tap-highlight mt-4 inline-block rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800"
          >
            Back to your computers
          </Link>
        </Card>
      </Layout>
    );
  }

  if (info.status === "denied" || info.status === "expired") {
    return (
      <Layout>
        <Card>
          <p className="font-medium text-slate-900">
            {info.status === "denied" ? "This request was denied." : "This request expired."}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Ask the PC to refresh its setup page and try again.
          </p>
          <Link
            to="/computers"
            className="no-tap-highlight mt-4 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Back to your computers
          </Link>
        </Card>
      </Layout>
    );
  }

  const expiresMs = new Date(info.expires_at).getTime();
  const remainingSec = Math.max(0, Math.floor((expiresMs - now) / 1000));
  const attemptsLeft = info.max_oob_attempts - info.failed_oob_attempts;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await approvePCEnrollmentSession(code, oob.trim());
      if (mounted.current) setApproved(true);
    } catch (err: unknown) {
      const r = (err as { response?: { data?: { detail?: string }; status?: number } })
        ?.response;
      if (r?.status === 410) {
        if (mounted.current) {
          setSubmitError("This request expired. Ask the PC to refresh and try again.");
        }
      } else if (r?.status === 429) {
        if (mounted.current) {
          setSubmitError("Too many wrong codes. Ask the PC to refresh and start over.");
        }
      } else {
        if (mounted.current) {
          setSubmitError(r?.data?.detail ?? "Couldn't approve. Try again.");
          // Refresh attempt counter so the user sees how many tries are left.
          loadInfo();
        }
      }
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }

  return (
    <Layout>
      <Card>
        <h1 className="text-lg font-semibold text-slate-900">
          Add this computer?
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          To {tenant?.name ?? "your office"}
        </p>

        <div className="mt-5 space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
          <Row label="Hostname" value={info.hostname_hint || "(not set)"} mono />
          <Row label="IP address" value={info.origin_ip} mono />
          <Row label="Browser" value={truncateUA(info.user_agent)} />
          <Row label="Requested" value={formatLocalTime(info.created_at)} />
          <Row
            label="Expires in"
            value={formatRemaining(remainingSec)}
            warning={remainingSec < 60}
          />
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700">
              Type the 4-digit code shown on the PC's screen
            </span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              autoFocus
              value={oob}
              onChange={(e) => setOob(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-2 block w-full rounded-lg border-2 border-slate-300 px-4 py-4 text-center font-mono text-3xl tracking-widest focus:border-slate-900 focus:outline-none"
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              Look at the PC. Don't ask someone to read it to you over the
              phone — that defeats the purpose.
            </p>
          </label>

          {submitError && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
              {submitError}
              {attemptsLeft > 0 && attemptsLeft < info.max_oob_attempts && (
                <span className="mt-1 block text-xs">
                  {attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} left.
                </span>
              )}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || oob.length !== 4 || remainingSec === 0}
            className="no-tap-highlight w-full rounded-lg bg-slate-900 py-4 text-base font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {submitting ? "Approving…" : "Approve"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-500">
          Not your PC? Just close this page. The request will expire on its own.
        </p>
      </Card>
    </Layout>
  );
}

function Row({
  label,
  value,
  mono,
  warning,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warning?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span
        className={`min-w-0 max-w-[60%] truncate text-right ${
          mono ? "font-mono" : ""
        } ${warning ? "font-semibold text-amber-700" : "text-slate-900"}`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function Card({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "ok" | "warn";
}) {
  const palette =
    tone === "ok"
      ? "border-green-200 bg-green-50"
      : tone === "warn"
        ? "border-red-200 bg-red-50"
        : "border-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border p-6 sm:p-8 ${palette}`}>{children}</div>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-start justify-center bg-slate-50 p-4 sm:p-8">
      <div className="w-full max-w-md sm:mt-12">
        <header className="mb-4 text-center">
          <div className="text-lg font-semibold text-slate-900">KraetdNet</div>
        </header>
        {children}
      </div>
    </main>
  );
}

function truncateUA(ua: string): string {
  if (!ua) return "(unknown)";
  // Try to extract the OS/browser hints from a long UA string.
  const m = ua.match(/\(([^)]+)\)/);
  if (m && m[1]) {
    return m[1].split(";")[0]?.trim() || ua.slice(0, 40);
  }
  return ua.slice(0, 40);
}

function formatLocalTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatRemaining(secs: number): string {
  if (secs <= 0) return "expired";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}
